import { addMinutes, isAfter, isNonNil, now, strongEntries } from '@shady-pines/types';
import { isNil } from 'lodash';
import { useEffect } from 'react';
import type { StoreApi } from 'zustand';
import { useEventStore } from '../event/event-store';
import { usePopupStore } from '../popup/popup-store';
import { useShowStore } from '../show/show-store';
import { useTrackStore } from '../track/track-store';
import { useUserStore } from '../user/user-store';
import { useApiStore, type ApiState } from './api-store';

const storeMap = {
  track: useTrackStore,
  user: useUserStore,
  show: useShowStore,
  event: useEventStore,
  popup: usePopupStore,
} as const satisfies { [k in keyof ApiState['localStores']]: StoreApi<any> };

export const useInitializeAndRefreshStores = (
  apiState: Omit<ApiState, 'localStoreRehydrationComplete'>,
) => {
  const setInitialApiState = useApiStore((s) => s.setInitialApiState);
  const rehydrationComplete = useApiStore((s) => s.localStoreRehydrationComplete);
  const setRefreshCompleted = useApiStore((s) => s.setRehydrationComplete);

  useEffect(() => {
    setInitialApiState(apiState);
    if (rehydrationComplete) {
      return;
    }

    void (async () => {
      const rehydrationConfig = await apiState.supabase
        ?.from('configurations')
        .select('*')
        .eq('name', 'rehydration_duration_minutes')
        .single();

      console.log('rehydration config!', rehydrationConfig);

      const rehydrationDurationMinutes = isNil(rehydrationConfig?.data?.parameter_number)
        ? null
        : Number(rehydrationConfig.data.parameter_number);

      await Promise.all(
        strongEntries(apiState.localStores).map(async ([key, localStore]) => {
          if (isNil(localStore)) {
            return;
          }
          const store = storeMap[key];
          await storeMap[key].persist.rehydrate();

          const lastDataFetchedFromApiAt = store.getState().lastDataFetchedFromApiAt;

          const storeExpired =
            isNonNil(lastDataFetchedFromApiAt) && isNonNil(rehydrationDurationMinutes)
              ? isAfter(now(), addMinutes(lastDataFetchedFromApiAt, rehydrationDurationMinutes))
              : false;
          console.log(
            'store is expired?',
            key,
            lastDataFetchedFromApiAt,
            rehydrationDurationMinutes,
            storeExpired,
          );

          if (storeExpired || key === 'track' || key === 'event') {
            store.setState(store.getInitialState() as any);
            console.log('cleared storage', key, store.getState());
            store.setState({
              lastDataFetchedFromApiAt: now(),
            });
            return;
          }

          if (isNil(lastDataFetchedFromApiAt)) {
            store.setState({
              lastDataFetchedFromApiAt: now(),
            });
          }
        }),
      );
      setRefreshCompleted();
    })();
  }, [apiState, rehydrationComplete, setInitialApiState, setRefreshCompleted]);
};
